import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';

const StyledIframe = styled.iframe`
  width: 100%;
  height: 800px;
`;

interface Props {
  contentKey: string;
}

function AppleRichContent({ contentKey }: Props) {
  const iframeSrc = `/sa/apple-rich-content/${contentKey}/index.html`;
  const iframeRef = useRef<HTMLIFrameElement>(null);
  let resizeTimeout: ReturnType<typeof setTimeout>;

  const hideIframeElements = () => {
    const contentWindow = iframeRef?.current?.contentWindow;

    if (!contentWindow) return;

    const links = Array.from(contentWindow.document.querySelectorAll('a'));
    const prices = Array.from(contentWindow.document.querySelectorAll('.price'));
    const paragraphs = Array.from(contentWindow.document.querySelectorAll('p'));

    links.forEach((link) => {
      link.remove();
    });

    prices.forEach((price) => {
      price.remove();
    });

    paragraphs.forEach((paragraph) => {
      if (paragraph.innerText.toLowerCase().includes('[your message]')) {
        paragraph.remove();
      }
    });
  };

  const resizeIframe = () => {
    clearTimeout(resizeTimeout);

    resizeTimeout = setTimeout(() => {
      const iframeHeight = iframeRef?.current?.contentWindow?.document?.body?.getBoundingClientRect().height;

      if (!iframeHeight) return;

      iframeRef.current.style.height = `${iframeHeight}px`;
    }, 250);
  };

  const resizeIframeOnSelectChange = () => {
    const selects = Array.from(iframeRef.current?.contentWindow?.document?.querySelectorAll('select') || []);

    selects.forEach((select) => select.addEventListener('change', () => resizeIframe()));
  };

  const resizeIframeOnImageLoad = () => {
    const images = Array.from(iframeRef.current?.contentWindow?.document?.querySelectorAll('img') || []);

    images.forEach((image) => image.addEventListener('load', resizeIframe));
  };

  useEffect(() => {
    const iframe = iframeRef?.current;

    if (!iframe) return;

    let loadEventCaptured = false;

    iframe.addEventListener('load', () => {
      loadEventCaptured = true;
      hideIframeElements();
      resizeIframe();
      resizeIframeOnImageLoad();
      resizeIframeOnSelectChange();
    });

    if (!loadEventCaptured) {
      hideIframeElements();
      resizeIframe();
      resizeIframeOnImageLoad();
      resizeIframeOnSelectChange();
    }

    window.addEventListener('resize', resizeIframe);
  }, []);

  return (
    <StyledIframe
      data-testid="apple-rich-content"
      ref={iframeRef}
      src={iframeSrc}
      scrolling="no"
      title="Product information from Apple"
    />
  );
}

export default AppleRichContent;
